<template>
  <div>
    <h4 class="table-page-header">
      CBS Programs
    </h4>

    <!-- Program Cards -->
    <div class="program-grid">
      <div
        v-for="program in programs"
        :key="program.id"
        class="program-card"
        :class="{ 'program-card--inactive': !program.active }"
      >
        <div class="program-card__header">
          <span class="program-card__name">{{ program.name }}</span>
          <el-tag size="mini" :type="program.active ? 'success' : 'info'">
            {{ program.active ? 'Active' : 'Inactive' }}
          </el-tag>
        </div>
        <div class="program-card__display-name">{{ program.display_name }}</div>
        <div class="program-card__description">{{ program.description }}</div>
        <div class="program-card__footer">
          <span class="program-card__count">
            <i class="fa fa-users"></i>
            {{ programClientCounts[program.id] || 0 }} clients
          </span>
          <el-button size="mini" type="primary" @click="viewProgram(program)">
            Manage Clients
          </el-button>
        </div>
      </div>
    </div>

    <!-- Manage Clients Modal -->
    <AIVDialog
      v-model="$modal.state.manage_program_clients"
      :title="selectedProgram ? `${selectedProgram.display_name} - Clients` : 'Program Clients'"
      width="90%"
      height="85vh"
    >
      <div v-if="selectedProgram">
        <!-- Add Client -->
        <div class="add-client-row">
          <AIVSelect
            v-model="clientsToAdd"
            :options="availableClientOptions"
            placeholder="Search and add clients..."
            :searchable="true"
            :multiSelect="true"
            class="add-client-select"
          />
          <el-button type="primary" size="small" @click="addClientToProgram" :disabled="clientsToAdd.length === 0">
            <i class="fa fa-plus"></i> Add
          </el-button>
        </div>

        <!-- Search -->
        <div class="filter-section">
          <div class="filter-row">
            <div class="filter-group search-group">
              <el-input
                v-model="clientSearch"
                placeholder="Search clients..."
                clearable
                @clear="clientSearch = ''"
                class="search-input"
              >
                <i
                  slot="prepend"
                  class="fa fa-search"
                  style="padding-left: 4px; padding-right: 4px"
                ></i>
              </el-input>
            </div>
          </div>
        </div>

        <!-- Current Clients Table -->
        <AIVTable
          :columns="programClientColumns"
          :data="filteredProgramClients"
          :pagination="programClientPagination"
          :item-name="'clients'"
          @pageChange="onClientPageChange"
          @pageSizeChange="onClientPageSizeChange"
        >
          <template #cell-actions="{ row }">
            <button
              class="btn btn-sm btn-danger"
              @click="removeClientFromProgram(row)"
              title="Remove"
            >
              <i class="fa fa-times"></i>
            </button>
          </template>
        </AIVTable>
      </div>
    </AIVDialog>
  </div>
</template>

<script>
import { cbsProgramsService } from "@/serviceHandlers/cbsProgramsService";
import { clientsService } from "@/serviceHandlers/clientsService";

export default {
  name: "CbsPrograms",
  data() {
    return {
      programs: [],
      programClientCounts: {},
      selectedProgram: null,
      programClientsAll: [],
      allClients: [],
      clientsToAdd: [],
      clientSearch: "",
      programClientColumns: [
        { key: "first_name", label: "First Name" },
        { key: "last_name", label: "Last Name" },
        { key: "actions", label: "", sortable: false },
      ],
      clientPage: 1,
      clientPageSize: 20,
    };
  },
  computed: {
    availableClients() {
      const currentIds = this.programClientsAll.map((c) => c.id);
      return this.allClients.filter((c) => !currentIds.includes(c.id));
    },
    availableClientOptions() {
      return this.availableClients.map((c) => ({
        id: c.id,
        name: `${c.first_name} ${c.last_name}`,
      }));
    },
    searchedProgramClients() {
      if (!this.clientSearch) return this.programClientsAll;
      const q = this.clientSearch.toLowerCase();
      return this.programClientsAll.filter(
        (c) =>
          (c.first_name || "").toLowerCase().includes(q) ||
          (c.last_name || "").toLowerCase().includes(q),
      );
    },
    filteredProgramClients() {
      const start = (this.clientPage - 1) * this.clientPageSize;
      return this.searchedProgramClients.slice(start, start + this.clientPageSize);
    },
    programClientPagination() {
      return {
        total: this.searchedProgramClients.length,
        perPage: this.clientPageSize,
        currentPage: this.clientPage,
        totalPages: Math.ceil(this.searchedProgramClients.length / this.clientPageSize) || 1,
      };
    },
  },
  async mounted() {
    await this.fetchPrograms();
    await this.fetchAllClients();
  },
  methods: {
    async fetchPrograms() {
      this.$spinner.show();
      try {
        this.programs = await cbsProgramsService.list();
        // Get client counts for each program
        for (const program of this.programs) {
          try {
            const clients = await cbsProgramsService.getClients(program.id);
            this.$set(this.programClientCounts, program.id, clients.length);
          } catch {
            this.$set(this.programClientCounts, program.id, 0);
          }
        }
      } catch (error) {
        this.$notifyError("Could not load programs: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    async fetchAllClients() {
      try {
        this.allClients = await clientsService.list({ activeStatus: "active", slim: true });
      } catch (error) {
        this.$notifyError("Could not load clients: " + error);
      }
    },
    async viewProgram(program) {
      this.selectedProgram = program;
      this.clientsToAdd = [];
      this.clientSearch = "";
      this.clientPage = 1;
      this.$spinner.show();
      try {
        this.programClientsAll = await cbsProgramsService.getClients(program.id);
      } catch (error) {
        this.$notifyError("Could not load program clients: " + error);
        this.programClientsAll = [];
      } finally {
        this.$spinner.hide();
      }
      this.$modal.open("manage_program_clients");
    },
    async addClientToProgram() {
      if (!this.clientsToAdd.length || !this.selectedProgram) return;
      this.$spinner.show();
      try {
        for (const clientId of this.clientsToAdd) {
          await cbsProgramsService.addClient(this.selectedProgram.id, clientId);
        }
        this.$notifySuccess(`${this.clientsToAdd.length} client(s) added successfully.`);
        this.programClientsAll = await cbsProgramsService.getClients(this.selectedProgram.id);
        this.$set(this.programClientCounts, this.selectedProgram.id, this.programClientsAll.length);
        this.clientsToAdd = [];
      } catch (e) {
        this.$notifyError("Failed to add one or more clients.");
      } finally {
        this.$spinner.hide();
      }
    },
    async removeClientFromProgram(client) {
      if (!this.selectedProgram) return;
      this.$spinner.show();
      try {
        await cbsProgramsService.removeClient(this.selectedProgram.id, client.id);
        this.$notifySuccess("Client removed from program");
        this.programClientsAll = await cbsProgramsService.getClients(this.selectedProgram.id);
        this.$set(this.programClientCounts, this.selectedProgram.id, this.programClientsAll.length);
      } catch (error) {
        this.$notifyError("Error removing client: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    onClientPageChange(page) {
      this.clientPage = page;
    },
    onClientPageSizeChange(size) {
      this.clientPageSize = size;
      this.clientPage = 1;
    },
  },
};
</script>

<style scoped>
.program-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}
.program-card {
  background: rgba(37, 55, 65, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1.25rem;
  transition: border-color 0.2s;
}
.program-card:hover {
  border-color: #447DF7;
}
.program-card--inactive {
  opacity: 0.6;
}
.program-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}
.program-card__name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
}
.program-card__display-name {
  color: #b3a6a6;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}
.program-card__description {
  color: #b3a6a6;
  font-size: 0.85rem;
  margin-bottom: 1rem;
}
.program-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.program-card__count {
  color: #b3a6a6;
  font-size: 0.9rem;
}
.add-client-row {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: center;
}
.add-client-select {
  flex: 1;
}
.filter-section {
  margin-bottom: 1rem;
}
.filter-row {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}
.filter-group.search-group {
  flex: 1;
  max-width: 300px;
}
</style>
